import { useMemo } from 'react'
import { z } from 'zod'

import { Beta } from '@mc/design-system'
import { paths } from '@mc/paths'

import { useSubcategoriesDeprecated } from 'libs/client/api'
import { ContentType } from 'types/data'

import { InstructorCarouselProps } from '../types'
import { InstructorTile, Pill } from './InstructorTile'

type CourseMeta = z.infer<typeof CourseMetaSchema>

export const MainCategoryInstructorCarousel = ({
  content,
  TitleComponent,
  categorySlug,
}: InstructorCarouselProps & {
  categorySlug: string
}) => {
  const { subcats } = useSubcategoriesDeprecated({ slug: categorySlug })

  const courseSlugToMeta = useMemo(() => {
    const slugToSubcat = (subcats || []).reduce(
      (acc, subcat) => {
        subcat.courses.forEach((courseSlug) => {
          if (!acc[courseSlug]) {
            acc[courseSlug] = []
          }
          acc[courseSlug].push({
            text: subcat.name,
            link: paths.subcategoryPage(subcat.slug),
          })
        })
        return acc
      },
      {} as Record<string, Pill[]>,
    )

    return content.reduce(
      (acc, course) => {
        const parsed = CourseMetaSchema.safeParse({
          ...course,
          courseId: course.id,
          courseSlug: course.slug,
          contentType: course.type,
          instructorHeadshotSrc: course.primary2x3,
          instructorImgSrc: course.instructorImage,
          instructorName: course.instructorName,
          courseTitle: course.title,
          outcomeSlugs: [],
          pills: (slugToSubcat?.[course.slug] || []).slice(0, 3),
          trailerId: course.brightcoveVideoId || '',
          trailerMediaUuid: course.mediaUuid,
        })
        if (parsed.success) {
          acc[course.slug] = parsed.data
        } else {
          // console.info('Failed to parse course meta', parsed.error.errors)
        }
        return acc
      },
      {} as Record<string, CourseMeta>,
    )
  }, [content, subcats])

  return (
    <Beta.CarouselModern
      title={TitleComponent}
      srLabel='instructor carousel'
      className='row mc-mb-3'
      arrowCls='d-none d-sm-block'
      pagesCls='d-none d-sm-block'
      overflow
    >
      {Object.entries(courseSlugToMeta).map(
        ([courseSlug, courseMeta], colIdx) => (
          <InstructorTile
            key={courseSlug}
            courseMeta={courseMeta}
            colIdx={colIdx}
            categoryName={categorySlug}
            subcatName={undefined}
            pills={courseMeta.pills}
          />
        ),
      )}
    </Beta.CarouselModern>
  )
}

const CourseMetaSchema = z.object({
  courseId: z.coerce.number(),
  courseSlug: z.string(),
  contentType: z.nativeEnum(ContentType),
  instructorHeadshotSrc: z.string(),
  instructorImgSrc: z.string(),
  instructorName: z.string(),
  courseTitle: z.string(),
  instructorBio: z.string(),
  outcomeSlugs: z.array(z.string()),
  pills: z.array(
    z.object({
      text: z.string(),
      link: z.string(),
    }),
  ),
  trailerId: z.string(),
  trailerMediaUuid: z.string().optional(),
})
